import React, { useState, useRef, useEffect } from 'react';
import Cookies from 'react-cookies';
import { COOKIES } from '../../brands/default/constants';
import { HEADER_SUBSCRIBE } from '../../brands/default/content/components/header';
import { trackEvent } from '../../helpers/analytics';
import {
  setSubscribedBrandWinCookie,
  setCookie,
  removeCookie,
} from '../../helpers/cookieSetter';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { subscribeUser } from '../../helpers/subscribe';
import { validateEmail } from '../../helpers/validations';
import WowModal from '../WowModal';
import ButtonForm from '../_generic/button/CTAButton';
import ConfirmationModal from '../_generic/confirmation/ConfirmationModal';
import InputForm from '../_generic/input/Input';
import useToast from '../_generic/toast/UseToast';
import HeaderIcon from './HeaderIcon';

/**
 * @param {string} message The error message to display
 */
const notifyError = (message) => {
  console.error(message);
  ErrorTrackingService({ message, name: HEADER_SUBSCRIBE.subscriptionError });
};

/**
 * Drop a 1-year CT cookie if the user is not logged in
 *
 * @param {object} customerToken the token returned by the API after subscription
 */
const handleCTCookie = (customerToken) => {
  // ct
  if (!Cookies.load(COOKIES.customerToken))
    Cookies.save(COOKIES.customerToken, customerToken, 365);
};

/**
 * A method to handle any post subscribe events
 *
 * @param {object} data The successful subscribe event
 */
const handleOnSubscribed = (data) => {
  // Handle CT cookie
  handleCTCookie(data.token);
  trackEvent('completed_subscription');
  if (data.newEmail) {
    trackEvent('newEmailEvent');
  } else {
    trackEvent('repeatEmailEvent');
  }
};

const HeaderSubscribe = () => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isDisabled, setDisabled] = useState(false);
  const [emailError, setEmailError] = useState('');

  const ref = useRef(null);
  const toast = useToast();
  const showToast = (message, status) => {
    toast.addToast(message, status);
  };

  const onClickIcon = (show) => {
    setEmailError('');
    setEmail('');
    setDisabled(false);
    setShowModal(show);
  };

  const confirmSubscription = () => {
    if (!validateEmail(email)) {
      return;
    }
    setShowConfirmationModal(true);
  };

  useEffect(() => {
    if (!email && !isDisabled) setDisabled(true);
    if (email && email.length > 0) {
      // eslint-disable-next-line no-negated-condition
      if (!validateEmail(email)) {
        setEmailError(HEADER_SUBSCRIBE.enterValidMail);
        if (!isDisabled) {
          setDisabled(true);
        }
      } else {
        setEmailError('');
      }
    }

    if (!email || !email.length) setEmailError('');

    if (email && email.length && !emailError && isDisabled) setDisabled(false);
  }, [email, isDisabled, emailError]);

  const setEmailReviewed = (event) => {
    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault();
      confirmSubscription();

      return;
    }
    setEmail(event.target.value);
  };

  const subscribe = async () => {
    try {
      const response = await subscribeUser(email);
      if (response?.status === 'success') {
        handleOnSubscribed(response);
        // BRAND-win (wowcher-win or livingsocial-win)
        setSubscribedBrandWinCookie();
        if (Cookies.load(COOKIES.userEmail)) {
          removeCookie(COOKIES.userEmail);
        }
        setCookie({
          expire: 365,
          key: COOKIES.userEmail,
          value: email,
        });
      } else {
        showToast(HEADER_SUBSCRIBE.toasterMsgInvalidEmail, 'toast-error');
        notifyError(response.data.message || HEADER_SUBSCRIBE.toasterMsg);
      }

      setShowModal(false);
      setShowConfirmationModal(false);

      return true;
    } catch (error) {
      console.error(HEADER_SUBSCRIBE.subscriptionError);
      ErrorTrackingService({
        message: error,
        name: HEADER_SUBSCRIBE.subscriptionError,
      });
      setShowModal(false);
      setShowConfirmationModal(false);

      return false;
    }
  };

  return (
    <>
      <div ref={ref} />

      <WowModal
        ariaLabelledBy="id-subscribe"
        backdropClassName="remove-backdrop"
        container={ref.current}
        extraClasses="remove-header subscribepopup move-backward"
        setShowModal={setShowModal}
        showModal={showModal}
        size="md"
        title=""
      >
        <div className="subscribe-input__input-container">
          <InputForm
            error={Boolean(emailError)}
            errorText=""
            extraClasses="remove-header subscribe"
            icon=""
            inputValue={email}
            name={'email'}
            normal={!emailError.length && !email}
            onChangeFunction={setEmailReviewed}
            placeholder={HEADER_SUBSCRIBE.yourEmail}
            type="email"
            valid={!emailError}
          />
          <ButtonForm
            className="remove-header subscribe uppercase"
            disabled={isDisabled}
            onClick={confirmSubscription}
          >
            {HEADER_SUBSCRIBE.signUp}
          </ButtonForm>
        </div>
      </WowModal>

      {showConfirmationModal && (
        <ConfirmationModal
          currentRef={ref.current}
          setShowConfirmationModal={setShowConfirmationModal}
          showConfirmationModal={showConfirmationModal}
          subscribe={subscribe}
        />
      )}
      <HeaderIcon
        clickMethod={() => onClickIcon(true)}
        icon="subscribe"
        isFirst={false}
        isMobileHidden
        linkId="id-subscribe"
      />
      <style jsx>{`
        .subscribe-input__input-container {
          position: relative;
          height: 40px;
          display: flex;
        }
      `}</style>
    </>
  );
};

export default HeaderSubscribe;
